/**
 * Application logging.
 *
 * The repo had 93 bare `console.log` calls in shipped code — order parameters,
 * deposit amounts, contract addresses, wallet errors and one full
 * transaction-history response — all of it printed to the console of every
 * user's browser in production. Nothing was guarded on NODE_ENV, and there was
 * no logger to guard them with. This is that logger.
 *
 * Two levels, deliberately, because the old code conflated them:
 *
 *   debug — developer instrumentation. Silent outside `npm start`. Use for
 *           anything whose audience is a developer reading a console.
 *   error — something went wrong. ALWAYS emitted, including in production,
 *           because an error nobody can see is an error nobody can fix.
 *   warn  — a recoverable irregularity. Always emitted, same reasoning.
 *
 * The distinction matters more than the noise reduction: a dozen call sites
 * were reporting genuine failures through `console.log`, which monitoring and
 * anyone reading a production console treats as chatter rather than a fault.
 *
 * NODE_ENV is "development" only under the dev server; it is "test" under Jest
 * and "production" in a build. So debug output is silent in tests too, which
 * keeps test output readable without any per-test mocking.
 */

/* eslint-disable no-console */

const isDevelopment = process.env.NODE_ENV === "development";

export const logger = {
  /** Developer instrumentation. Silent outside development. */
  debug: (...args: unknown[]): void => {
    if (isDevelopment) console.log(...args);
  },

  /** A recoverable irregularity. Always emitted. */
  warn: (...args: unknown[]): void => {
    console.warn(...args);
  },

  /** A failure. Always emitted, so production problems stay visible. */
  error: (...args: unknown[]): void => {
    console.error(...args);
  },
};

export default logger;
